import _ from "lodash";
export const keyArgsForPagination = filters => {
  if (filters) {
    return _.keys(_.omit(filters, "after"));
  }
  return [];
};

/**
 * Prepares a key for pagination by removing the 'after' cursor from filters.
 * Note: 'after' or any pagination filter must be a child of the 'filters' object,
 * not a sibling of 'filters' at the top level of fieldVariables.
 */
export const keyArgsForPaginationWithFilters = (fieldVariables = {}) => {
  if (fieldVariables) {
    const filtersWithoutEndCursor = {
      ...fieldVariables,
      filters: _.omit(fieldVariables.filters, ["after"]),
    };

    return JSON.stringify(filtersWithoutEndCursor);
  }
  return "";
};

export const keyArgsForACLGlobalControlParametersPagination = (
  fieldVariables = {}
) => {
  if (fieldVariables) {
    const filtersWithoutEndCursor = {
      ...fieldVariables,
      filters: {
        ...fieldVariables.filters,
        paginationFilters: _.omit(fieldVariables.filters.paginationFilters, [
          "after",
        ]),
      },
    };

    return JSON.stringify(filtersWithoutEndCursor);
  }
  return "";
};

export const keyArgsForGradingCommentTemplates = (filters = {}) => {
  return ["id", "type", "filters", keyArgsForPagination(filters), "paginated"];
};

export const getGradingCommentTemplatesField = () => ({
  keyArgs: args => keyArgsForGradingCommentTemplates(args?.filters),
  merge: (existing = {}, incoming = {}, { readField, args }) => {
    const filters = args?.filters || {};
    return mergeForCommentListPagination({
      existing,
      incoming,
      readField,
      filters,
    });
  },
});

export const mergeForCommentListPagination = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);
  if (_.isEmpty(after) || !existing) {
    return incoming;
  }

  const incomingTotalCount = readField("totalCount", incoming);
  const existingEdges = readField("edges", existing) ?? [];
  const incomingEdges = readField("edges", incoming) ?? [];
  const incomingPageInfo = readField("pageInfo", incoming) ?? {};
  const createdByStaff = readField("createdByStaff", incoming);

  const updatedEdges = _.uniqBy([...existingEdges, ...incomingEdges], item => {
    const node = readField("node", item);
    return readField("id", node);
  });

  return {
    __typename: readField("__typename", incoming),
    edges: updatedEdges,
    totalCount: incomingTotalCount,
    pageInfo: incomingPageInfo,
    createdByStaff,
  };
};

export const mergeForPagination = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);

  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};

    const updatedEdges = _.uniqBy(
      [...existingEdges, ...incomingEdges],
      item => {
        const node = readField("node", item);

        return readField("id", node);
      }
    );

    return {
      edges: updatedEdges,
      totalCount: incomingTotalCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const mergeForCurriculumWebsiteNodePagination = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);

  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const incomingTotalEnabledCount = readField("enabledCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};

    const updatedEdges = _.uniqBy(
      [...existingEdges, ...incomingEdges],
      item => {
        const node = readField("node", item);

        return readField("id", node);
      }
    );

    return {
      edges: updatedEdges,
      totalCount: incomingTotalCount,
      enabledCount: incomingTotalEnabledCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const mergeForPaginationWithUnitPlans = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);

  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};
    const incomingUnitTypeCount = readField("unitTypeCount", incoming) ?? [];

    const updatedEdges = _.uniqBy(
      [...existingEdges, ...incomingEdges],
      item => {
        const node = readField("node", item);

        return readField("id", node);
      }
    );

    return {
      edges: updatedEdges,
      totalCount: incomingTotalCount,
      pageInfo: incomingPageInfo,
      unitTypeCount: incomingUnitTypeCount,
    };
  }
  return incoming;
};

/**
 * Prepares a key for pagination by removing the 'after' cursor from filters.
 * Note: 'after' or any pagination filter must be a child of the 'filters' object,
 * not a sibling of 'filters' at the top level of fieldVariables.
 */
export const mergeForPaginationWithFilters = ({
  existing,
  incoming,
  fieldVariables = {},
  readField,
}) => {
  const after = _.get(fieldVariables, "filters.after", null);
  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};

    return {
      edges: [...existingEdges, ...incomingEdges],
      totalCount: incomingTotalCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const mergeAccessibleUserForPagination = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);

  if (!after) {
    return incoming;
  }

  const incomingData = _.first(incoming);
  const incomingTotalCount = readField("totalCount", incomingData);
  const existingUsersRef = existing
    ? readField("users", _.first(existing))
    : null;
  const incomingUsersRef = readField("users", incomingData);
  const existingEdges = existingUsersRef
    ? readField("edges", existingUsersRef) ?? []
    : [];
  const incomingEdges = incomingUsersRef
    ? readField("edges", incomingUsersRef) ?? []
    : [];
  const incomingPageInfo = readField("pageInfo", incomingUsersRef) ?? {};

  const extractNodeId = edge => {
    const nodeRef = readField("node", edge);
    return nodeRef ? readField("id", nodeRef) : null;
  };

  const updatedEdges = _.uniqBy(
    [...existingEdges, ...incomingEdges],
    extractNodeId
  );

  const userType = readField("userType", incomingData);
  const sharedWithUser = readField("sharedWithUser", incomingData);
  const totalCount = readField("totalCount", incomingData);

  return [
    {
      userType,
      sharedWithUser,
      totalCount,
      users: {
        edges: updatedEdges,
        totalCount: incomingTotalCount,
        pageInfo: incomingPageInfo,
        __typename: "AccessibleUsersConnection",
      },
    },
  ];
};

export const mergeArrays = (existing = [], incoming, { mergeObjects }) => {
  return _.map(incoming, (data, index) => {
    return mergeObjects(existing[index], data);
  });
};

export const mergeForACLGlobalControlParametersPagination = ({
  existing,
  incoming,
  fieldVariables = {},
  readField,
}) => {
  const after = _.get(fieldVariables, "filters.paginationFilters.after", null);
  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};

    return {
      edges: [...existingEdges, ...incomingEdges],
      totalCount: incomingTotalCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const mergeForOrganizationFeedPagination = ({
  existing,
  incoming,
  readField,
  filters,
}) => {
  const after = _.get(filters, "after", null);

  if (after) {
    const incomingTotalCount = readField("totalCount", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};
    const incomingProgramAnalytics =
      readField("programAnalytics", incoming) ?? {};

    const updatedEdges = _.uniqBy(
      [...existingEdges, ...incomingEdges],
      item => {
        const node = readField("node", item);
        return readField("id", node);
      }
    );

    return {
      programAnalytics: { ...incomingProgramAnalytics },
      edges: updatedEdges,
      totalCount: incomingTotalCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const getLearningCourseField = () => ({
  keyArgs: args => {
    if (args) {
      return keyArgsForPagination(args);
    }
  },
  merge: (existing = {}, incoming = {}, { readField, args }) => {
    if (args) {
      return mergeForLearningCoursePagination({
        existing,
        incoming,
        readField,
        filters: args,
      });
    }
    return incoming;
  },
});

export const mergeForLearningCoursePagination = ({
  existing,
  incoming,
  filters = {},
  readField,
}) => {
  const after = _.get(filters, "after", null);

  if (after) {
    const incomingTotalCount = readField("count", incoming);
    const existingEdges = readField("edges", existing) ?? [];
    const incomingEdges = readField("edges", incoming) ?? [];
    const incomingPageInfo = readField("pageInfo", incoming) ?? {};

    const updatedEdges = _.uniqBy(
      [...existingEdges, ...incomingEdges],
      item => {
        const node = readField("node", item);
        return readField("id", node);
      }
    );

    return {
      edges: updatedEdges,
      count: incomingTotalCount,
      pageInfo: incomingPageInfo,
    };
  }
  return incoming;
};

export const replaceMacroEnumWithLabels = ({ comment }) => {
  const MACRO_ENUM_TO_LABEL = {
    FIRST_NAME: "First name",
    LAST_NAME: "Last name",
    MIDDLE_NAME: "Middle name",
    PREFERRED_NAME: "Preferred name",
    DISPLAY_NAME: "Display name",
    HE_SHE_THEY: "He/She/They",
    HIM_HER_THEM: "Him/Her/Them",
    HIS_HER_THEIR: "His/Her/Their",
  };
  const transformMacros = ({ comment, regex, transformFn }) => {
    return _.replace(comment, regex, transformFn);
  };
  const MACRO_STYLE = `color: #008392; 
    font-family: AvenirNext, Avenir Next, AvenirNextWorld, Arial, sans-serif, system-ui; 
    font-size: 14px; 
    font-style: normal; 
    font-weight: 500; 
    line-height: 20px;`;
  return transformMacros({
    comment,
    regex: /<macro>(.*?)<\/macro>/gi,
    transformFn: (match, p1) => {
      const enumValue = _.trim(p1);
      const label = MACRO_ENUM_TO_LABEL[enumValue];
      return label
        ? `<macro contenteditable="false" style="${MACRO_STYLE}">{{${label}}}</macro>`
        : match;
    },
  });
};
